import getCroppedImg from '@/helpers/cropImage'
import { useTranslation } from 'next-i18next/pages'
import theme, { colors } from '@/theme'
import {
  AvatarBadge,
  AvatarProps,
  Box,
  Button,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  useDisclosure,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState, useCallback } from 'react'
import { PiPencilSimple } from 'react-icons/pi'
import Cropper from 'react-easy-crop'
import { Avatar } from '@/components/atoms/Avatar'
import { API_GATEWAY } from '@/constants/constants'
import { AvatarSet } from './AvatarSet'
import PlusIcon from '@/public/assets/icons/plus.svg'

interface EditorAvatarProps extends AvatarProps {
  padding?: number
  onAvatarChange: (value: string) => void
}

export const uploadImage = async (file: Blob) => {
  try {
    const data = new FormData()
    data.append('avatar', file)

    const res = await fetch(API_GATEWAY + '/avatars', {
      method: 'POST',
      body: data,
    })
    const resData = await res.json()

    return resData.cid
  } catch (error) {
    console.log(error)
  }
}

export const EditorAvatar = ({ src, padding, onAvatarChange, ...props }: EditorAvatarProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [previewImg, setPreviewImg] = useState(src)

  const handleFileChange = async (_event: any) => {
    let reader = new FileReader()

    reader.onload = function (e: any) {
      setPreviewImg(e.target.result)
      onOpen()
    }

    reader.readAsDataURL(inputRef.current?.files?.item(0) as Blob)

    inputRef.current!.value = ''
  }

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const { mutateAsync: uploadMutationAsync, isPending } = useMutation({
    mutationFn: uploadImage,
    onSuccess: onClose,
  })

  const onSaveClick = useCallback(async () => {
    const croppedImage = await getCroppedImg(previewImg!, croppedAreaPixels as any)
    const result = await uploadMutationAsync(croppedImage!)

    onAvatarChange(result)
  }, [previewImg, croppedAreaPixels])

  return (
    <>
      <Flex flexShrink={0} w={{ base: 24 }} h={{ base: 24 }} justifyContent="center">
        <Box padding={`10px`} w={'full'} bgGradient={theme.colors.gradient.primary} rounded={'full'}>
          <Box rounded={'full'} h={'full'} ring={`${6}px`} ringColor={'ninja.600'}>
            <Avatar {...props} w={'full'} size={{ base: '24' }} src={src} bg={'ninja.100'} h={'100%!important'}>
              <AvatarBadge
                onClick={() => inputRef.current?.click()}
                bgImage={colors.gradient.primary}
                cursor={'pointer'}
                _hover={{
                  bgGradient: colors.gradient.secondary,
                  filter: `drop-shadow(0 4px 25px rgba(110, 97, 253, .51))`,
                  color: 'white',
                }}
                border={0}
                boxSize="28px"
              >
                <PiPencilSimple size="18px" />
              </AvatarBadge>
            </Avatar>
          </Box>
        </Box>
      </Flex>
      <SimpleGrid width={{ base: 'auto' }} gap={{ base: 2 }} columns={{ base: 3 }} overflowX={{ base: 'auto' }}>
        <AvatarSet onClick={onAvatarChange} />
        <Flex
          justifyContent={'center'}
          alignItems={'center'}
          w={{ base: '52px' }}
          rounded={'full'}
          border={'1px dashed'}
          cursor={'pointer'}
          borderColor={'whiteAlpha.400'}
          onClick={() => inputRef.current?.click()}
        >
          <PlusIcon />
        </Flex>
      </SimpleGrid>
      <Modal size={'lg'} isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('profile.avatar')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody height={96}>
            <Box mb="8" position={'relative'} height={80} gap={8}>
              <Cropper
                image={previewImg}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape={'round'}
                showGrid={false}
                onCropComplete={onCropComplete}
                onCropChange={setCrop}
                onZoomChange={setZoom}
              />
            </Box>
            <Flex mx={8} gap={8}>
              <Text color="ninja.200" fontSize="2xl">
                -
              </Text>
              <Slider aria-label="slider-ex-2" min={1} max={3} step={0.1} defaultValue={1} onChange={(e) => setZoom(e)}>
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <Text color="ninja.200" fontSize="2xl">
                +
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button isLoading={isPending} width={'full'} colorScheme="blue" mr={3} onClick={onSaveClick}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <input style={{ display: 'none' }} accept="image/*" ref={inputRef} type="file" onChange={handleFileChange} />
    </>
  )
}
